// src/lib/api/services/services.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, validatePositiveNumber, sanitizeInput } from '../shared/validation'
import { getOrCreatePriceAssistedCategory } from './categories'

/**
 * Get all services for a company
 * Migrated from supabaseApi.js lines 2503-2520
 * @param {string} [providedCompanyId] - Optional company ID (avoids internal lookup)
 */
const _getServices = async (providedCompanyId = null) => {
  const companyId = providedCompanyId || await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      service_categories(name)
    `)
    .eq('company_id', companyId)
    .order('sort_order', { ascending: true })

  if (error) throw error

  return createSuccessResponse(data || [])
}

/**
 * Create a new service
 * Migrated from supabaseApi.js lines 2522-2545
 */
const _createService = async (serviceData) => {
  validateRequired(serviceData.name, 'Service name')
  validateRequired(serviceData.price, 'Service price')
  validatePositiveNumber(serviceData.price, 'Service price')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('User not authenticated')
  }

  const sanitizedData = {
    ...serviceData,
    name: sanitizeInput(serviceData.name),
    description: serviceData.description ? sanitizeInput(serviceData.description) : null,
    company_id: companyId,
    created_by: userId
  }

  const { data, error } = await supabase
    .from('services')
    .insert([sanitizedData])
    .select()
    .single()

  if (error) throw error

  return data
}

/**
 * Update an existing service
 * Migrated from supabaseApi.js lines 2547-2565
 */
const _updateService = async (serviceId, serviceData) => {
  validateRequired(serviceId, 'Service ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  if (serviceData.price) {
    validatePositiveNumber(serviceData.price, 'Service price')
  }
  
  const sanitizedData = {
    ...serviceData,
    company_id: companyId
  }

  if (sanitizedData.name) {
    sanitizedData.name = sanitizeInput(sanitizedData.name)
  }
  if (sanitizedData.description) {
    sanitizedData.description = sanitizeInput(sanitizedData.description)
  }

  const { data, error } = await supabase
    .from('services')
    .update(sanitizedData)
    .eq('id', serviceId)
    .select()
    .single()

  if (error) throw error

  return data
}

/**
 * Delete a service
 * Migrated from supabaseApi.js lines 2567-2580
 */
const _deleteService = async (serviceId) => {
  validateRequired(serviceId, 'Service ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('services')
    .delete()
    .eq('id', serviceId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get service by ID
 */
const _getServiceById = async (serviceId) => {
  validateRequired(serviceId, 'Service ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      service_categories(name, description)
    `)
    .eq('id', serviceId)
    .eq('company_id', companyId)
    .single()

  if (error) throw error

  return data
}

/**
 * Add service to estimate
 * Migrated from supabaseApi.js lines 2690-2705
 */
const _addServiceToEstimate = async (estimateId, serviceId, quantity = 1, customPrice = null, notes = null) => {
  validateRequired(estimateId, 'Estimate ID')
  validateRequired(serviceId, 'Service ID')
  validatePositiveNumber(quantity, 'Quantity')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase.rpc('add_service_to_estimate', {
    p_estimate_id: estimateId,
    p_service_id: serviceId,
    p_quantity: quantity,
    p_custom_price: customPrice,
    p_notes: notes,
    p_company_id: companyId
  })
  
  if (error) throw error
  return data
}

/**
 * Duplicate a service
 * Migrated from supabaseApi.js lines 2720-2735
 */
const _duplicateService = async (serviceId, newName = null) => {
  validateRequired(serviceId, 'Service ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase.rpc('duplicate_service', {
    p_service_id: serviceId,
    p_new_name: newName,
    p_company_id: companyId
  })
  
  if (error) throw error
  return data
}

/**
 * Get popular services
 * Migrated from supabaseApi.js lines 2737-2750
 */
const _getPopularServices = async (limit = 10) => {
  validatePositiveNumber(limit, 'Limit')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('Company ID not found')
  }

  const { data, error } = await supabase.rpc('get_popular_services', {
    p_company_id: companyId,
    p_limit: limit
  })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Search services
 */
const _searchServices = async (query, companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      service_categories(name)
    `)
    .eq('company_id', resolvedCompanyId)
    .or(`name.ilike.%${query.replace(/[,%\\()]/g, '')}%,description.ilike.%${query.replace(/[,%\\()]/g, '')}%`)
    .order('sort_order', { ascending: true })

  if (error) throw error

  return createSuccessResponse(data || [])
}

// Export wrapped functions with error handling
/**
 * Create a service from AI price assistant
 * Automatically assigns to "Price assisted" category and adds tag
 */
const _createAIService = async (serviceData) => {
  validateRequired(serviceData.name, 'Service name')
  validateRequired(serviceData.price, 'Service price')
  validatePositiveNumber(serviceData.price, 'Service price')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('User not authenticated')
  }

  // Get or create "Price assisted" category
  const category = await getOrCreatePriceAssistedCategory()

  // Get existing tags or initialize empty array
  const existingTags = Array.isArray(serviceData.tags) ? serviceData.tags : []
  const tags = existingTags.includes('Price assisted')
    ? existingTags
    : [...existingTags, 'Price assisted']

  const sanitizedData = {
    name: sanitizeInput(serviceData.name),
    description: serviceData.description ? sanitizeInput(serviceData.description) : null,
    price: serviceData.price,
    price_type: 'fixed',
    service_category_id: category.id,
    tags: tags,
    company_id: companyId,
    created_by: userId,
    is_active: true
  }

  const { data, error } = await supabase
    .from('services')
    .insert([sanitizedData])
    .select()
    .single()
  
  if (error) throw error
  
  return data
}

// Export wrapped functions with error handling
export const getServices = withErrorHandling(_getServices, 'fetch services', 'services')
export const createService = withErrorHandling(_createService, 'create service', 'service')
export const createAIService = withErrorHandling(_createAIService, 'create AI service', 'service')
export const updateService = withErrorHandling(_updateService, 'update service', 'service')
export const deleteService = withErrorHandling(_deleteService, 'delete service', 'service')
export const getServiceById = withErrorHandling(_getServiceById, 'get service by ID', 'service')
export const addServiceToEstimate = withErrorHandling(_addServiceToEstimate, 'add service to estimate', 'service')
export const duplicateService = withErrorHandling(_duplicateService, 'duplicate service', 'service')
export const getPopularServices = withErrorHandling(_getPopularServices, 'get popular services', 'services')
export const searchServices = withErrorHandling(_searchServices, 'search services', 'services')